import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// 15-second IG promo montage of Sweat With Slay.
// Records at 1080x1920. IG Reels covers the top ~220px (handle/UI)
// and bottom ~360px (caption + actions), so all content lives in a
// vertically-centered safe band (~1080×1340). Use Playwright recorder
// at /app/scripts/record_promo.py to render an MP4.

const SAFE_TOP = 360;
const SAFE_BOTTOM = 580;

const SLIDES = [
  {
    id: "logo",
    duration: 2400,
    bg: "#FF1B91",
    render: () => (
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-white/85 uppercase tracking-[0.5em] text-base font-semibold mb-12">
          Strength · Style · Slay
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="font-display font-black uppercase tracking-tighter leading-[0.85] text-white"
          style={{ fontSize: "200px" }}
        >
          Sweat
          <br />
          With
          <br />
          <span className="font-brush font-normal normal-case inline-block -rotate-3" style={{ fontSize: "240px" }}>Slay</span>
        </motion.h1>
      </div>
    ),
  },
  {
    id: "intro",
    duration: 2400,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / The coach
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "140px" }}>
          Obsessed
          <br />
          with <span className="text-[#FF1B91]">watching</span>
          <br />
          people win.
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.55 }} className="mt-10 text-white/65 text-2xl leading-snug">
          Strength-first coaching<br />for anyone who refuses to play small.
        </motion.p>
      </div>
    ),
  },
  {
    id: "programs",
    duration: 2600,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / Three tiers
        </motion.p>
        {[
          { label: "Essentials", price: "$89", desc: "Programming + form library" },
          { label: "Coaching", price: "$179", desc: "Weekly check-ins + custom plan" },
          { label: "VIP", price: "$399", desc: "1:1 strategy + voice notes" },
        ].map((t, i) => (
          <motion.div
            key={t.label}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.2 + i * 0.18 }}
            className="border border-white/15 bg-[#0F0F0F] p-6 mb-4 flex items-center justify-between"
          >
            <div className="min-w-0">
              <p className="font-display font-black uppercase text-white text-4xl tracking-tighter">{t.label}</p>
              <p className="text-white/55 text-lg mt-1">{t.desc}</p>
            </div>
            <p className="font-display font-black text-[#FF1B91] text-4xl tracking-tighter shrink-0 ml-4">{t.price}</p>
          </motion.div>
        ))}
      </div>
    ),
  },
  {
    id: "everfit",
    duration: 2200,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / Inside the app
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "130px" }}>
          Coaching<br />
          <span className="text-[#FF1B91]">that travels</span><br />
          with you.
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.55 }} className="mt-10 text-white/65 text-2xl leading-snug">
          Delivered through Everfit.<br />Lifts logged. Form reviewed.
        </motion.p>
      </div>
    ),
  },
  {
    id: "perks",
    duration: 2200,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / Perks unlocked
        </motion.p>
        <div className="grid grid-cols-2 gap-4">
          {["Bucked Up", "VTL", "Psycho", "Built Bar", "Legion", "Naked"].map((b, i) => (
            <motion.div
              key={b}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              className="border border-[#FF1B91]/40 bg-[#FF1B91]/5 py-6 text-center"
            >
              <p className="font-display font-black uppercase text-white text-2xl tracking-tighter">{b}</p>
              <p className="text-[#FF1B91] text-[10px] uppercase tracking-[0.3em] font-bold mt-1">Member code</p>
            </motion.div>
          ))}
        </div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1.1 }} className="mt-8 text-white/55 text-xl text-center">
          + more inside.
        </motion.p>
      </div>
    ),
  },
  {
    id: "cta",
    duration: 3200,
    bg: "#FF1B91",
    render: () => (
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="text-white/85 uppercase tracking-[0.5em] text-sm font-bold mb-10">
          / Ready?
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
          style={{ fontSize: "240px" }}
        >
          Pull
          <br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "260px" }}>up.</span>
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }} className="mt-10 text-white text-4xl font-bold tracking-tight">
          sweatwithslay.com
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.85 }} className="mt-3 text-white/85 uppercase tracking-[0.4em] text-base font-semibold">
          @sweatwithslay
        </motion.p>
      </div>
    ),
  },
];

const TOTAL = SLIDES.reduce((acc, s) => acc + s.duration, 0); // ~15s

export default function Promo() {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.setAttribute("data-promo-started", "true");
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      let total = 0;
      let active = 0;
      for (let i = 0; i < SLIDES.length; i++) {
        total += SLIDES[i].duration;
        if (elapsed < total) { active = i; break; }
        active = i;
      }
      setIdx(active);
      setProgress(Math.min(1, elapsed / TOTAL));
      if (elapsed < TOTAL) {
        raf = requestAnimationFrame(tick);
      } else {
        document.body.setAttribute("data-promo-done", "true");
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const slide = SLIDES[idx];

  return (
    <main
      data-testid="promo-page"
      data-promo-active={slide.id}
      className="font-body overflow-hidden fixed inset-0"
      style={{ background: slide.bg, color: "#fff" }}
    >
      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
      />

      {/* Slide content lives in the IG-safe band */}
      <AnimatePresence>
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="absolute left-0 right-0 px-16"
          style={{ top: SAFE_TOP, bottom: SAFE_BOTTOM }}
        >
          {slide.render()}
        </motion.div>
      </AnimatePresence>

      {/* Progress bar — placed inside top safe zone so IG doesn't cover it */}
      <div className="absolute left-12 right-12 h-1 bg-white/15" style={{ top: SAFE_TOP - 30 }}>
        <div
          className="h-full bg-white"
          style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }}
        />
      </div>

      {/* Wordmark inside bottom safe zone */}
      <div
        className="absolute left-0 right-0 text-center text-white/70 text-base uppercase tracking-[0.4em] font-semibold"
        style={{ bottom: SAFE_BOTTOM - 60 }}
      >
        sweatwithslay.com
      </div>
    </main>
  );
}
